import { Card, CardContent, Stack, Typography } from "@mui/material";
import type {
  GridCellParams,
  GridRowParams,
  MuiEvent,
} from "@mui/x-data-grid-premium";
import React, { useContext, useEffect, useState } from "react";
import { PatientContext } from "../../contexts/PatientContext.tsx";
import { ProjectContext } from "../../contexts/ProjectContext";
import { useFetch } from "../../services/hooks/useFetch.ts";
import { apiPrefix, getDiffDaysMessage } from "../../utils/main";
import IMDRFCategorize from "./categorize/IMDRFCategorize";
import PatientAttributeDataGrid from "./categorize/PatientAttributeDataGrid";
import PatientCardSkeleton from "./skeletons/PatientCardSkeleton";
import FlowsheetDataTable from "./tables/FlowsheetDataTable.tsx";
import LabDataTable from "./tables/LabDataTable.tsx";
import MedicationsDataExpandTable from "./tables/MedicationsDataExpandTable";
import ProblemsDataExpandTable from "./tables/ProblemsDataExpandTable.jsx";
import PromDataExpandTable from "./tables/PromDataExpandTable.jsx";

export default function PatientCard() {
  const [isOpen, setIsOpen] = useState(false);
  const [attributeToEdit, setAttributeToEdit] = useState<
    PatientAttribute | undefined
  >();
  const [mark, setMark] = useState<Element | null>(null);
  const {
    id: projectId,
    defaultPreIndexDays,
    defaultPostIndexDays,
    medicationsEnabled,
  } = useContext(ProjectContext) || {};

  const {
    data: projectPromGroups,
    fetch: fetchProjectPromGroups,
    isLoading: projectPromGroupsLoading,
  } = useFetch<PromGroup[]>();

  useEffect(() => {
    if (!projectPromGroups && !projectPromGroupsLoading) {
      void fetchProjectPromGroups(`${apiPrefix}/projects/${projectId}/proms`);
    }
  }, [
    projectPromGroups,
    projectPromGroupsLoading,
    fetchProjectPromGroups,
    projectId,
  ]);

  const patient = useContext(PatientContext);

  function handleAttributeEdit(
    params: GridCellParams | GridRowParams,
    e: MuiEvent<React.MouseEvent<HTMLElement>>,
  ) {
    setIsOpen(true);
    const attribute = patient?.attributes.find((a) => a.id === params.row.id);
    setAttributeToEdit(attribute);
    setMark(e.currentTarget);
  }

  function handlePopOverClose() {
    setIsOpen(false);
  }

  const [isPromEnabled, setIsPromEnabled] = useState(false);

  useEffect(() => {
    if (
      !projectPromGroupsLoading &&
      projectPromGroups &&
      projectPromGroups.length > 0
    ) {
      setIsPromEnabled(true);
    } else {
      setIsPromEnabled(false);
    }
  }, [projectPromGroups, projectPromGroupsLoading]);

  if (!patient || !patient.id) {
    return <PatientCardSkeleton />;
  }

  function getDeceasedMessage(patient: PatientContextType) {
    let deceasedMessage;
    if (patient.deathFlag && patient.deathDiffDays === null) {
      deceasedMessage = "Yes, date not provided";
    } else if (
      (patient.deathFlag ||
        (!patient.deathFlag && patient.deathDiffDays !== null)) &&
      patient.deathDiffDays &&
      defaultPreIndexDays &&
      defaultPostIndexDays &&
      patient.deathDiffDays >= -defaultPreIndexDays &&
      patient.deathDiffDays <= defaultPostIndexDays
    ) {
      // date is within specified project window
      deceasedMessage = "Yes, ";
      deceasedMessage += getDiffDaysMessage(patient.deathDiffDays);
    }
    return deceasedMessage;
  }

  return (
    <Card className={"patient-card"}>
      <CardContent
        sx={{ padding: 0, paddingBottom: "0 !important", textAlign: "left" }}
      >
        <Stack className={"content"} flexDirection={"column"}>
          <Typography className={"title"} variant="subtitle1">
            Patient Info
          </Typography>
          <div
            className="column-for-demographics-data"
            style={{ paddingTop: "15px" }}
          >
            <div className="row" style={{ width: "100%" }}>
              <div className="column">
                <div className="row">
                  <Typography variant="body2">Age</Typography>
                  <Typography className={"bold-weight"} variant="body2">
                    {patient.age}
                  </Typography>
                </div>
                <div className="row">
                  <Typography variant="body2">Gender</Typography>
                  <Typography className={"bold-weight"} variant="body2">
                    {patient.gender}
                  </Typography>
                </div>
              </div>
              <div className="column">
                <div className="row">
                  <Typography variant="body2">Race</Typography>
                  <Typography className={"bold-weight"} variant="body2">
                    {patient.race}
                  </Typography>
                </div>
                <div className="row">
                  <Typography variant="body2">Ethnicity</Typography>
                  <Typography className={"bold-weight"} variant="body2">
                    {patient.ethnicity}
                  </Typography>
                </div>
              </div>
            </div>
          </div>
          <div className="column" style={{ paddingTop: "5px" }}>
            {getDeceasedMessage(patient) && (
              <div className="row">
                <Typography variant="body2">Deceased</Typography>
                <Typography className={"bold-weight"} variant="body2">
                  {getDeceasedMessage(patient)}
                </Typography>
              </div>
            )}
          </div>
        </Stack>

        <div className="column-for-structured-data">
          <PatientAttributeDataGrid
            title={"Categorizations"}
            subtitle={" for all visits"}
            handleRowEdit={handleAttributeEdit}
          />

          <Typography className={"patient-row-title"} variant="h6">
            <span className={"patient-row-title-font"}>Patient Data</span>
            <span className={"patient-row-subtitle-font"}>
              &nbsp;for all visits
            </span>
          </Typography>
          <ProblemsDataExpandTable />
          {medicationsEnabled && <MedicationsDataExpandTable />}
          {isPromEnabled && <PromDataExpandTable />}
          <LabDataTable
            encounterId={null}
            countTotal={patient.labCount?.total}
            highlightTotal={patient.labCount?.highlight}
          />
          <FlowsheetDataTable
            encounterId={null}
            countTotal={patient.flowsheetCount?.total}
            highlightTotal={patient.flowsheetCount?.highlight}
          />
        </div>
      </CardContent>
      {isOpen && (
        <IMDRFCategorize
          anchorEl={mark}
          anchorVertical={"center"}
          onClose={handlePopOverClose}
          open={true}
          attribute={attributeToEdit}
        />
      )}
    </Card>
  );
}
